import { useState, useEffect, useMemo } from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import _pick from 'lodash/pick'
import _isArray from 'lodash/isArray'
import HeaderFilterButton from 'app/ProviderSearch/HeaderFilterButton'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import styles from './index.module.scss'
import { filterTitle } from 'constants/common'
import PsychNoticeModal from 'app/ProviderSearch/PsychNoticeModal'
import { RESTRICTED_STIMULANT_STATES } from 'app/Bookings/constants'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import useThriveAuth from 'hooks/useThriveAuth'
import useGetTreatment from 'hooks/treatments/useGetTreatment'
const MultiselectDropdown = dynamic(() => import('common/MultiselectDropdown'))

type AreaOfConcernFilterProps = {
  onFilterSelect: (selectedFilter: Record<string, unknown>) => void
  groupedFilters: GroupedProviderFilter
  containerClassName?: string
  isAdhdFilterSelected?: boolean
}

const AreaOfConcernFilter: React.FC<AreaOfConcernFilterProps> = ({
  onFilterSelect,
  groupedFilters,
  containerClassName = '',
  isAdhdFilterSelected = false,
}) => {
  const { isSupportOrThirdPartyUser } = useThriveAuth()
  const router = useRouter()
  const { query } = router
  const [showDropdown, setShowDropdown] = useState<boolean>(false)
  const [showPsychNoticeModal, setShowPsychNoticeModal] = useState<boolean>(
    false
  )
  const [appliedAreasOfConcern, setAppliedAreasOfConcerns] = useState<string[]>(
    []
  )

  const allAreasOfConcern = groupedFilters[ProviderFilterTypes.AREA_OF_CONCERN]

  const showPsychModalTreatment = useGetTreatment(
    TreatmentFlags.COE_PYSCH_MODAL,
    true,
    {
      support: isSupportOrThirdPartyUser,
    }
  )

  const isShowPsychModalTreatmentON =
    showPsychModalTreatment === treatmentFlagValues.SHOW

  const currentState = query?.state as string

  const isRestrictedState = RESTRICTED_STIMULANT_STATES.includes(currentState)

  // show psych modal only for Support user in restricted state on 'ADHD' area of concern filter selection
  const showPsychModal =
    isRestrictedState &&
    isShowPsychModalTreatmentON &&
    !showDropdown &&
    isAdhdFilterSelected

  useEffect(() => {
    const areasIdsInQuery = _pick(query, 'areaOfConcern[]')['areaOfConcern[]']

    if (!areasIdsInQuery && !appliedAreasOfConcern.length) {
      return
    }

    if (!areasIdsInQuery) {
      //When Clear button in pill was clicked
      setAppliedAreasOfConcerns([])
      return
    }

    if (!_isArray(areasIdsInQuery)) {
      //In case only one Area of Concern was Selected
      setAppliedAreasOfConcerns([areasIdsInQuery as string])
    } else {
      setAppliedAreasOfConcerns([...(areasIdsInQuery as string[])])
    }
  }, [query])

  useEffect(() => {
    if (showPsychModal) {
      setShowPsychNoticeModal(true)
    }
  }, [showPsychModal])

  const onApply = (selectedAreas: string[]) => {
    onFilterSelect({ areaOfConcern: selectedAreas })
    setAppliedAreasOfConcerns(selectedAreas)
  }

  const onHidePsychNoticeModal = () => {
    setShowPsychNoticeModal(false)
  }

  const areaOfConcernStringValue = useMemo(() => {
    return appliedAreasOfConcern.reduce((concat, areaOfConcernId, index) => {
      const areaOfConcernFilter = allAreasOfConcern.find(
        (areaOfConcern) => areaOfConcern.id === areaOfConcernId
      )

      if (areaOfConcernFilter) {
        if (index === 0) {
          return areaOfConcernFilter.value
        }

        return `${concat}, ${areaOfConcernFilter.value}`
      }

      return concat
    }, '')
  }, [appliedAreasOfConcern])

  return (
    <>
      <HeaderFilterButton
        title={filterTitle.SPECIALTY}
        showDropdown={showDropdown}
        setShowDropdown={setShowDropdown}
        filterType={ProviderFilterTypes.AREA_OF_CONCERN}
        text={areaOfConcernStringValue}
        placeHolder="I want help for..."
        mobilePlaceHolder="Help for"
        buttonClassName={styles.helpfor_filter_button}
        containerClassName={containerClassName}
      >
        <MultiselectDropdown
          filterTypeText="specialties"
          filterList={allAreasOfConcern}
          appliedFilters={appliedAreasOfConcern}
          onApply={onApply}
          containerClassName="top-6 mt-5"
        />
      </HeaderFilterButton>
      {showPsychNoticeModal && (
        <PsychNoticeModal
          showModal={showPsychNoticeModal}
          onHide={onHidePsychNoticeModal}
        />
      )}
    </>
  )
}

export default AreaOfConcernFilter
